/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { DIFFICULTY_LEVELS } from "./learning";

// Course offering status
export const COURSE_OFFERING_STATUS = {
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export type CourseOfferingStatus = typeof COURSE_OFFERING_STATUS[keyof typeof COURSE_OFFERING_STATUS];

// Course Definition - the template/blueprint for a course
export const courseDefinitions = pgTable("course_definitions", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  content: text("content").notNull(), // syllabus, curriculum outline, etc.
  topicIds: integer("topic_ids").array(), // concepts covered
  skillIds: integer("skill_ids").array(), // skills taught/practiced
  thumbnailUrl: text("thumbnail_url"),
  estimatedDuration: integer("estimated_duration"), // estimated duration in hours or days
  difficultyLevel: text("difficulty_level"), // beginner, intermediate, advanced
  prerequisites: text("prerequisites").array(), // prerequisites for the course
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Course Offering - the instantiated course with specific dates, instructor, location
export const courseOfferings = pgTable("course_offerings", {
  id: serial("id").primaryKey(),
  courseDefinitionId: integer("course_definition_id").notNull(), // reference to course definition
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  instructorId: integer("instructor_id").notNull(), // reference to instructor user
  curriculumManagerId: integer("curriculum_manager_id"), // optional curriculum manager
  location: text("location"), // physical location or "online"
  enrollmentCapacity: integer("enrollment_capacity"),
  enrollmentCount: integer("enrollment_count").default(0),
  status: text("status").default("scheduled"), // scheduled, in-progress, completed, cancelled
  price: integer("price"), // if applicable
  assessmentIds: integer("assessment_ids").array(), // assessments for this offering
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schemas
export const insertCourseDefinitionSchema = createInsertSchema(courseDefinitions)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    difficultyLevel: z.enum([
      DIFFICULTY_LEVELS.BEGINNER,
      DIFFICULTY_LEVELS.INTERMEDIATE,
      DIFFICULTY_LEVELS.ADVANCED,
      DIFFICULTY_LEVELS.EXPERT
    ]).optional()
  });

export const insertCourseOfferingSchema = createInsertSchema(courseOfferings)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    status: z.enum([
      COURSE_OFFERING_STATUS.SCHEDULED,
      COURSE_OFFERING_STATUS.IN_PROGRESS,
      COURSE_OFFERING_STATUS.COMPLETED,
      COURSE_OFFERING_STATUS.CANCELLED
    ]).optional()
  });

// Types
export type CourseDefinition = typeof courseDefinitions.$inferSelect;
export type InsertCourseDefinition = z.infer<typeof insertCourseDefinitionSchema>;
export type CourseOffering = typeof courseOfferings.$inferSelect;
export type InsertCourseOffering = z.infer<typeof insertCourseOfferingSchema>; 